import { Modal, Skeleton } from "@mui/material";
import AuthPage from "components/shared/AuthPage";
import ExpandedHelpdeskIcon from "components/shared/ExpandedHelpdeskIcon";
import ModalBase from "components/shared/ModalBase";
import PreviewDeclaration from "components/shared/PreviewDeclaration";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IDeclaration } from "store/contributionStore";
import { fetchContributions, updateDeclaration } from "store/contributionStore";
import { useAppDispatch, useAppSelector } from "store/store";

import { getDeclarationCardsData } from "./cards_helpers";
import CompanyForm from "./CompanyForm";
import CurrentDeclarations from "./CurrentDeclarations";
import ValidateDeclarationModal from "./CurrentDeclarations/ValidateDeclarationModal";
import DASDownloadModal from "./DASDownloadModal";
import DeclarationModal from "./DeclarationModal";
import DeclarationsHistory from "./DeclarationsHistory";
import styles from "./style.module.scss";
import UpcomingDeclarations from "./UpcomingDeclarations";
import type { DeclarationsCardsData } from "./utils";

/**
 * Specific page for downloading and having history of all URSSAF declarations. Now separate from Payment page.
 */
const DeclarationPage = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const { companyInfos } = useCompanySettings();

    const [modalType, setModalType] = useState("");
    const [dasDownloadModal, setDasDownloadModal] = useState(false);
    const [currentDeclaration, setCurrentDeclaration] = useState<IDeclaration | DeclarationsCardsData>();
    const [lastDeclarationDone, setLastDeclarationDone] = useState<number>();

    const { contributionList, contributionListStatus } = useAppSelector((state) => state.contribution);
    const { payments } = useAppSelector((state) => state.payment);

    useEffect(() => {
        if (contributionListStatus === "idle") dispatch(fetchContributions());
    }, [contributionListStatus]);

    /**
     * Opens modal to see preview modal with declaration already done.
     */
    const previewAction = async (contribution: IDeclaration) => {
        await setCurrentDeclaration(contribution);
        setModalType("preview");
    };

    /**
     * Opens modal to see declaration data and download it.
     */
    const declareAction = async (contribution: IDeclaration) => {
        await setCurrentDeclaration(contribution);

        if (contribution.type === "urssaf") {
            setModalType("declare");
        } else {
            setDasDownloadModal(true);
        }
    };

    /**
     * Triggers action to save date in the declareAt value -> means they have actually deposited file on URSSAF website.
     */
    const saveAction = async (contribution: IDeclaration) => {
        await setCurrentDeclaration(contribution);

        if (contribution.type === "urssaf") {
            setModalType("setAsDone");
        } else {
            dispatch(updateDeclaration({ declarationId: contribution.id, data: { declaredAt: new Date() } }));
        }
    };

    /**
     * Formatted data from contributionList as needed to use within ActionCard component.
     */
    const cardsData: DeclarationsCardsData[] = getDeclarationCardsData({
        contributionList,
        previewAction,
        declareAction,
        saveAction,
    });

    const isCompanySetupForExport = useMemo(() => {
        return (
            !!companyInfos?.apeCode &&
            !!companyInfos?.address &&
            !!companyInfos?.postalCode &&
            !!companyInfos?.city &&
            !!companyInfos?.country &&
            !!companyInfos?.activityType &&
            !!companyInfos?.phoneNumber
        );
    }, [companyInfos]);

    // Whether company's info are complete to be able to download DAS2.
    const [isCompanyInfoComplete, setIsCompanyInfoComplete] = useState(isCompanySetupForExport);

    // Whether there are any declarations in history/future to display that block.
    const historyCardsExist = cardsData.filter((card) => card.timeStatus === "history").length > 0;
    const futureCardsExist = cardsData.filter((card) => card.timeStatus === "future").length > 0;

    return (
        <AuthPage userAuthenticated pageName="declarations">
            <div className={styles["declarations-container"]}>
                <div className={styles["declarations-page-header"]}>
                    <h1>{t("Declarations.PageTitle")}</h1>
                    <ExpandedHelpdeskIcon
                        link="https://helpdesk.crealo.app/fr/article/telecharger-la-declaration-des-cotisations-sociales-urssaf-aeiczw/"
                        text={t("Declarations.Helpdesk")}
                    />
                </div>

                <div className={styles["urssaf-container"]}>
                    {contributionListStatus === "idle" || contributionListStatus === "pending" ? (
                        <div className={styles["spinner-container"]}>
                            <Skeleton width={1000} height={130} />
                            <Skeleton width={1000} height={130} />
                            <Skeleton width={300} height={20} />
                            <Skeleton width={1000} height={130} />
                            <Skeleton width={1000} height={130} />
                        </div>
                    ) : (
                        <>
                            <CurrentDeclarations cards={cardsData} setCurrentDeclaration={setCurrentDeclaration} />
                            {futureCardsExist && (
                                <UpcomingDeclarations
                                    cards={cardsData.filter((card) => card.timeStatus === "future").slice(0, 3)}
                                />
                            )}

                            {historyCardsExist && (
                                <DeclarationsHistory
                                    cards={cardsData.filter((card) => card.timeStatus === "history")}
                                    lastDeclarationDone={lastDeclarationDone}
                                    setLastDeclarationDone={setLastDeclarationDone}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
            <Modal open={modalType === "declare"} onClose={() => setModalType("")}>
                <DeclarationModal
                    setModalType={setModalType}
                    declarationData={currentDeclaration}
                    handleClose={() => setModalType("")}
                />
            </Modal>
            <ModalBase
                modalTitle={
                    currentDeclaration && currentDeclaration?.quarter > 0
                        ? t("Declarations.ValidateModal.TitleQuarter", {
                              quarter: currentDeclaration?.quarter,
                              year: currentDeclaration?.year,
                          })
                        : t("Declarations.ValidateModal.TitleQuarter", { year: currentDeclaration?.year })
                }
                open={modalType === "setAsDone"}
                onClose={() => setModalType("")}
            >
                <ValidateDeclarationModal
                    declarationData={currentDeclaration}
                    handleClose={() => setModalType("")}
                    setLastDeclarationDone={setLastDeclarationDone}
                />
            </ModalBase>
            <PreviewDeclaration
                showModal={modalType === "preview"}
                onClose={() => setModalType("")}
                contributionId={currentDeclaration?.id}
            />
            <ModalBase
                modalTitle={t("Declarations.DAS2.CompleteInfoTitle")}
                modalSubtitle={t("Declarations.DAS2.CompleteInfoSubtitle")}
                open={modalType === "updateCompany"}
                onClose={() => setModalType("")}
            >
                <CompanyForm
                    company={companyInfos}
                    handleClose={() => {
                        setModalType("");
                        setIsCompanyInfoComplete(true);
                    }}
                />
            </ModalBase>
            <ModalBase
                modalTitle={t("Declarations.DAS2.ModalTitle", { year: currentDeclaration?.year })}
                open={dasDownloadModal}
                onClose={() => setDasDownloadModal(false)}
            >
                <DASDownloadModal
                    isCompanyInfoComplete={isCompanyInfoComplete}
                    setModalType={setModalType}
                    declarationData={currentDeclaration}
                    handleClose={() => setDasDownloadModal(false)}
                />
            </ModalBase>
        </AuthPage>
    );
};

export default DeclarationPage;
